import React from "react"
import { Box, ButtonBase, SvgIcon, Typography, useTheme } from "@mui/material"
import type { SvgIconProps } from "@mui/material/SvgIcon"
import { selectionFocusVisibleOutline } from "@/theme/designTokens"

function ChevronDownIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" aria-hidden fontSize="small">
      <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
    </SvgIcon>
  )
}

export type OrderDisclosureToggleHeaderProps = {
  /** Stable id for aria-controls on the controlled region. */
  panelId: string
  expanded: boolean
  onToggle: () => void
  title: React.ReactNode
  /** Optional right-aligned summary (e.g. chip) — kept outside the focus target to avoid nested buttons. */
  endAdornment?: React.ReactNode
  /** Optional subtitle under title when collapsed. */
  secondary?: React.ReactNode
  /** Accessible name when title is not plain text. */
  ariaLabel: string
  /** Order-details default is start. Checkout order-total disclosure uses end. */
  chevronPlacement?: "start" | "end"
}

/**
 * Keyboard-accessible header row: full-width toggle + optional adornment sibling.
 */
export function OrderDisclosureToggleHeader({
  panelId,
  expanded,
  onToggle,
  title,
  endAdornment,
  secondary,
  ariaLabel,
  chevronPlacement = "start",
}: OrderDisclosureToggleHeaderProps) {
  const theme = useTheme()
  const buttonId = `${panelId}-trigger`
  const tEnter = theme.transitions.duration.standard
  const tExit = theme.transitions.duration.shorter
  const easeOut = theme.transitions.easing.easeOut
  const easeSharp = theme.transitions.easing.sharp
  const chevronSx = {
    flexShrink: 0,
    color: theme.palette.action.active,
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: expanded ? tEnter : tExit,
      easing: expanded ? easeOut : easeSharp,
    }),
  } as const

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        width: "100%",
      }}
    >
      <ButtonBase
        id={buttonId}
        component="button"
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={ariaLabel}
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: theme.spacing(1),
          flex: 1,
          minWidth: 0,
          minHeight: 48,
          textAlign: "left",
          py: theme.spacing(0.5),
          px: 0,
          borderRadius: theme.shape.borderRadius,
          color: "inherit",
          "&:focus-visible": {
            ...selectionFocusVisibleOutline(theme, parseFloat(theme.spacing(0.5)) || 4),
          },
        }}
      >
        {chevronPlacement === "start" ? <ChevronDownIcon sx={chevronSx} /> : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {typeof title === "string" ? (
            <Typography
              component="span"
              variant="subtitle2"
              sx={{ fontWeight: theme.typography.fontWeightBold, display: "block" }}
            >
              {title}
            </Typography>
          ) : (
            title
          )}
          {secondary ? (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, display: "block", mt: theme.spacing(0.25) }}
            >
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {chevronPlacement === "end" ? <ChevronDownIcon sx={chevronSx} /> : null}
      </ButtonBase>
      {endAdornment ? (
        <Box sx={{ flexShrink: 0, alignSelf: "center", pt: theme.spacing(0.25) }}>{endAdornment}</Box>
      ) : null}
    </Box>
  )
}
